import { readFile, writeFile } from 'node:fs/promises';
import { resolve } from 'node:path';

interface BankEntry {
  name: string;
  url: string | string[];
  bus?: string;
  loop?: boolean;
}

interface BankManifest {
  /** Optional default bus that all entries fall back to. */
  defaultBus?: string;
  /** Sound entries — keyed by name or as an array. */
  sounds: BankEntry[] | Record<string, Omit<BankEntry, 'name'>>;
}

/**
 * Read a sound manifest JSON and emit a typed module:
 *
 *   - the union of every sound name (`SoundName`)
 *   - a `loadBank(engine)` function that calls `engine.loadSound` for each
 *
 * Drop the generated file into your repo and `engine.sound("coin")` will be
 * autocompleted as a literal type.
 */
export async function runGen(argv: string[]): Promise<void> {
  const args = parseArgs(argv);
  const raw = await readFile(args.input, 'utf-8');
  const manifest: BankManifest = JSON.parse(raw);
  const entries = normalize(manifest);
  const out = render(entries, manifest.defaultBus);
  await writeFile(args.out, out, 'utf-8');
  process.stdout.write(`Wrote ${args.out} (${entries.length} entries).\n`);
}

interface ParsedArgs {
  input: string;
  out: string;
}

function parseArgs(argv: string[]): ParsedArgs {
  const out: ParsedArgs = { input: '', out: 'bank.gen.ts' };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i]!;
    if (a === '--out') out.out = argv[++i] ?? out.out;
    else out.input = a;
  }
  if (!out.input) {
    process.stderr.write('Usage: zvuk gen <bank.json> [--out bank.gen.ts]\n');
    process.exit(1);
  }
  out.input = resolve(out.input);
  out.out = resolve(out.out);
  return out;
}

function normalize(manifest: BankManifest): BankEntry[] {
  if (Array.isArray(manifest.sounds)) return manifest.sounds;
  return Object.entries(manifest.sounds).map(([name, entry]) => ({ name, ...entry }));
}

function render(entries: BankEntry[], defaultBus: string | undefined): string {
  const names = entries.map((e) => e.name);
  const lines: string[] = [];
  lines.push('// AUTOGENERATED by `zvuk gen`. Do not edit.');
  lines.push("import type { Engine, LoadSoundOptions } from '@schmooky/zvuk';");
  lines.push('');
  lines.push('export type SoundName =');
  for (let i = 0; i < names.length; i++) {
    lines.push(`  | ${JSON.stringify(names[i])}${i === names.length - 1 ? ';' : ''}`);
  }
  lines.push('');
  lines.push('export const BANK = [');
  for (const e of entries) {
    const url = JSON.stringify(e.url);
    const opts: string[] = [];
    if (e.bus) opts.push(`bus: ${JSON.stringify(e.bus)}`);
    if (e.loop) opts.push('loop: true');
    lines.push(
      `  { name: ${JSON.stringify(e.name)}, url: ${url}${opts.length ? `, options: { ${opts.join(', ')} }` : ''} },`,
    );
  }
  lines.push('] as const;');
  lines.push('');
  lines.push('export interface BankLoadOptions extends Omit<LoadSoundOptions, "bus"> {}');
  lines.push('');
  lines.push(
    'export async function loadBank(engine: Engine, options: BankLoadOptions = {}): Promise<void> {',
  );
  lines.push('  await Promise.all(');
  lines.push('    BANK.map((entry) =>');
  if (defaultBus) {
    lines.push(
      `      engine.loadSound(entry.name, entry.url, { bus: ${JSON.stringify(defaultBus)}, ...entry.options, ...options }),`,
    );
  } else {
    lines.push('      engine.loadSound(entry.name, entry.url, { ...entry.options, ...options }),');
  }
  lines.push('    ),');
  lines.push('  );');
  lines.push('}');
  lines.push('');
  return lines.join('\n');
}
